/* eslint-disable react/require-default-props */
import React, {
  useState, memo, useMemo, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { CSSTransition } from 'react-transition-group';
import { IconButton, Icon } from '@picsio/ui';
import {
  Settings, CheckIcon, AddTeammates, Copy,
  ExportCsv,
} from '@picsio/icons';
import { useSelector } from 'react-redux';
import showExportCsvDialog from '../../../../helpers/showExportCsvDialog';
import * as utils from '../../../../shared/utils';
import localization from '../../../../shared/strings';
import picsioConfig from '../../../../../../../config';
import { Input, Checkbox } from '../../../../UIComponents';

import useShareAssets from '../../../../helpers/components/hooks/useShareAssets';
import Logger from '../../../../services/Logger';
import { navigate } from '../../../../helpers/history';
import UpgradePlan from '../../../UpgradePlan';
import isAssetImageChecker from '../../helpers/isAssetImageChecker';
import sendEventToIntercom from '../../../../services/IntercomEventService';
import copyTextToClipboard from '../../../../helpers/copyTextToClipboard';
import { showDialog } from '../../../dialog';
import Tooltip from '../../../Tooltip';

import styles from './styles.module.scss';

const LOCAL_STORAGE_KEY_BULK_DIALOG_CONFIRM = 'picsio.hideDialogConfirmBulkSasUnpublish';

const Share = (props) => {
  const [isFocused, setFocused] = useState(false);
  const {
    selectedAssetsIds,
    assetsData,
    asset,
    assetSharingAllowed,
    onChange,
    isVisible,
    inProgress,
    toggleVisibility,
    disabled = false,
    isPreview = false,
  } = props;
  const { share } = useShareAssets('details panel');
  const assetId = asset?._id;
  const singleSharingSettings = asset?.singleSharingSettings;
  let alias = asset?.singleSharingSettings?.alias;
  const fileExtension = asset?.fileExtension;
  const assetsCount = selectedAssetsIds.length;
  const isShared = assetsCount === 1 ? singleSharingSettings?.isShared : assetsData.sharedAsSasCount === assetsCount;
  const isBulkSharing = useMemo(() => assetsCount > 1, [assetsCount]);
  const link = `${picsioConfig.websitesBaseUrl}/preview/${assetId}`;
  const thumbnailLink = `${picsioConfig.apiBaseUrl}/preview/${assetId}/thumbnail`;
  const isAssetImage = isAssetImageChecker(fileExtension);
  const isManageTeam = useSelector((state) => state.user.role.permissions.manageTeam);
  const teamSettings = useSelector((state) => state.user?.team?.settings || {});
  const { branding, customDomains } = useSelector((state) => state.user.subscriptionFeatures || {});
  const customDomainsAllowed = branding || customDomains;
  const url = new URL(link);

  if (!customDomainsAllowed && alias) {
    alias = alias.split('/').toSpliced(0, 1, url.host).join('/');
  }

  const [copied, setCopied] = useState({ shareLink: false, directLink: false });

  const handleChange = (value) => {
    const isRestricted = asset?.restrictSettings?.isRestricted || assetsData.isRestricted;

    function doShare() {
      if (assetsCount > 1 && value) {
        share();
      } else {
        const toUpdate = { isShared: value };
        if (value && !alias) toUpdate.alias = `${url.host}/preview/${assetId}`;

        onChange(selectedAssetsIds, toUpdate);
      }

      if (!isVisible) toggleVisibility('detailsAssetShareVisibility');

      sendEventToIntercom('share from ipanel');

      const eventData = {
        assets_count: assetsCount,
        share_type: isBulkSharing ? 'bulk' : 'single',
        assetIds: selectedAssetsIds,
      };
      if (value) {
        Logger.log('User', 'InfoPanelAssetShare', eventData);
      } else {
        Logger.log('User', 'InfoPanelAssetUnShare', eventData);
      }
    }

    if (isRestricted && !isShared) {
      /** Restricted assets detected for sharing */
      showDialog({
        title: localization.DIALOGS.RESTRICTED_ASSET_DETECTED.TITLE,
        text: localization.DIALOGS.RESTRICTED_ASSET_DETECTED.TEXT,
        textBtnOk: localization.DIALOGS.RESTRICTED_ASSET_DETECTED.BTN_OK,
        textBtnCancel: localization.DIALOGS.RESTRICTED_ASSET_DETECTED.BTN_CANCEL,
        onOk: () => doShare(),
      });
    } else if (!isPreview && isShared) {
      /** Unshare from catalog view */
      const showDialogBulkUnpublish = utils.LocalStorage.get(LOCAL_STORAGE_KEY_BULK_DIALOG_CONFIRM) !== true;

      if (showDialogBulkUnpublish) {
        Logger.log('User', 'SasUnpublishModal', {
          assets_number: assetsCount,
        });
        showDialog({
          title: localization.DIALOGS.BULK_SAS_UNPUBLISH.title,
          text: localization.DIALOGS.BULK_SAS_UNPUBLISH.text,
          textBtnCancel: localization.DIALOGS.BULK_SAS_UNPUBLISH.textBtnCancel,
          textBtnOk: localization.DIALOGS.BULK_SAS_UNPUBLISH.textBtnOk,
          onOk: ({ checkbox }) => {
            if (checkbox) {
              utils.LocalStorage.set(LOCAL_STORAGE_KEY_BULK_DIALOG_CONFIRM, checkbox);
            }
            doShare();
            Logger.log('User', 'SasUnpublishModalAction', {
              action: 'confirmed',
              assets_number: assetsCount,
            });
          },
          onCancel: () => {
            Logger.log('User', 'SasUnpublishModalAction', {
              action: 'cancelled',
              assets_number: assetsCount,
            });
          },
          checkbox: {
            value: false,
            label: localization.DIALOGS.BULK_SAS_UNPUBLISH.checkboxLabel,
          },
        });
      } else {
        doShare();
      }
    } else {
      doShare();
    }
  };

  const handleTitleClick = () => toggleVisibility('detailsAssetShareVisibility');

  const handleInputFocus = () => setFocused(true);

  const handleInputBlur = () => setFocused(false);

  const openSettings = useCallback(() => {
    Logger.log('User', 'InfoPanelShareAssetSettingsClicked', {
      assets_count: assetsCount,
      share_type: isBulkSharing ? 'bulk' : 'single',
      assetIds: selectedAssetsIds,
    });
    navigate(`/singlesharing/${assetsCount > 1 ? 'bulk' : assetId}`);
  }, [assetId, assetsCount, isBulkSharing, selectedAssetsIds]);

  const openTeammates = () => {
    Logger.log('User', 'InfoPanelShareSingleInviteTeammate');
    navigate('/teammates?tab=teammates');
  };

  const copyDirectLink = () => {
    Logger.log('User', 'InfoPanelShareSingleCopyDirectLink');
    const assetShareURL = thumbnailLink;
    setCopied((prev) => ({ ...prev, directLink: true }));
    setTimeout(() => {
      setCopied((prev) => ({ ...prev, directLink: false }));
    }, 2000);
    copyTextToClipboard(assetShareURL);
  };

  const copyShareLink = () => {
    Logger.log('User', 'InfoPanelShareSingleCopyShareLink');
    const assetShareURL = alias ? `https://${alias}` : link;
    setCopied((prev) => ({ ...prev, shareLink: true }));
    setTimeout(() => {
      setCopied((prev) => ({ ...prev, shareLink: false }));
    }, 2000);
    copyTextToClipboard(assetShareURL);
  };

  const isDirectLinkVisible = useMemo(() => {
    // If not an image or not shared, always false
    if (!isAssetImage || !isShared || assetsCount > 1) {
      return false;
    }

    const allowDeprecatedEmbeddingsUrls = teamSettings?.allowDeprecatedEmbeddingsUrls;

    // If empty or no allowDeprecatedEmbeddingsUrls in settings, return false
    if (!allowDeprecatedEmbeddingsUrls) {
      return false;
    }

    // If value is 'notAllowed', return false
    if (allowDeprecatedEmbeddingsUrls === 'notAllowed') {
      return false;
    }

    // If value is 'allowedOnlyOld', check additional condition shareUpdatedAt < dateToReleaseCDN
    if (allowDeprecatedEmbeddingsUrls === 'allowedOnlyOld') {
      const shareUpdatedAt = new Date(singleSharingSettings?.updatedAt);
      const dateToReleaseCDN = new Date('2025-07-09T09:01:59.625Z');
      return shareUpdatedAt < dateToReleaseCDN;
    }

    // If value is 'allowedOldAndNew', return true
    if (allowDeprecatedEmbeddingsUrls === 'allowedOldAndNew') {
      return true;
    }

    // Default case: return false
    return false;
  }, [singleSharingSettings?.updatedAt, isAssetImage, isShared, teamSettings?.allowDeprecatedEmbeddingsUrls, assetsCount]);

  const handleClickExportCsv = useCallback((event) => {
    /** If event is triggered by keyboard - we want to handle only Enter key */
    if (event.type === 'keydown' && event.key !== 'Enter') return;

    showExportCsvDialog({ assetIds: selectedAssetsIds, initiationLocation: 'share block' });
  }, [selectedAssetsIds]);

  return (
    <div
      data-qa="details-component-share"
      className={cn('detailsPanel__item', { act: isVisible, disabled })}
    >
      <div className="detailsPanel__title">
        <span
          className={cn('detailsPanel__title_text', { withoutTriangle: (!isShared && !isBulkSharing) || !assetSharingAllowed })}
          onClick={isShared || isBulkSharing ? handleTitleClick : null}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && isShared && handleTitleClick()}
        >
          {localization.DETAILS.textShare}
          <If condition={!assetSharingAllowed}>
            <UpgradePlan text={localization.UPGRADE_PLAN.textForHigherPlan} />
          </If>
        </span>
        <div className="detailsPanel__title_buttons">
          <If condition={isShared && !disabled && assetSharingAllowed}>
            <If condition={isManageTeam}>
              <Tooltip
                content="Invite people to collaborate"
                placement="top"
              >
                <IconButton
                  id="button-inviteTeammates"
                  onClick={openTeammates}
                  size="lg"
                  componentProps={{
                    'data-testid': 'btnInviteTeammates',
                  }}
                >
                  <AddTeammates />
                </IconButton>
              </Tooltip>
            </If>
            <Tooltip content="Asset sharing settings" placement="top">
              <IconButton
                id="button-openSettings"
                onClick={openSettings}
                size="lg"
                componentProps={{
                  'data-testid': 'btnOpenSettings',
                }}
              >
                <Settings />
              </IconButton>
            </Tooltip>
          </If>
          <If condition={!disabled}>
            <Checkbox
              slide
              inProgress={inProgress}
              value={isShared}
              onChange={handleChange}
              icon="share"
              disabled={!assetSharingAllowed}
            />
          </If>
        </div>
      </div>
      <CSSTransition in={isVisible && (isShared || isBulkSharing)} timeout={300} classNames="fade">
        <>
          <Choose>
            <When condition={isBulkSharing && isVisible}>
              <div className={styles.bulkBlock}>
                <p>
                  ({assetsData.sharedAsSasCount} of {assetsCount} selected assets already shared)
                </p>
                <If condition={assetsData.sharedAsSasCount}>
                  <div className="fieldCopyTo">
                    <div className="fieldCopyToHolderCDN notEmpty">
                      <Input
                        value="To get all shared links utilize export CSV functionality"
                        disabled
                        className="fieldCopyToUrl"
                      />
                      <IconButton
                        onClick={handleClickExportCsv}
                        size="lg"
                        className="fieldCopyToSettings"
                        componentProps={{
                          'data-testid': 'iconExportCSVSharedAssets',
                        }}
                      >
                        <ExportCsv />
                      </IconButton>
                    </div>
                  </div>
                </If>
              </div>
            </When>
            <Otherwise>
              <If condition={isVisible && assetSharingAllowed && isShared}>
                <div className="sharingLink">{localization.DETAILS.textShareLink}</div>
                <div className={cn('fieldCopyTo', { isFocused })}>
                  <div className="fieldCopyToHolder">
                    <Input
                      value={alias ? `https://${alias}` : link}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      className="fieldCopyToUrl"
                    />
                    <Tooltip content={copied.shareLink ? 'Copied!' : 'Copy link'} placement="top">
                      <IconButton
                        onClick={copyShareLink}
                        size="lg"
                        className="fieldCopyToSettings"
                        componentProps={{
                          'data-value': alias ? `https://${alias}` : link,
                          'data-testid': 'iconCopySharingLink',
                        }}
                      >
                        <Choose>
                          <When condition={copied.shareLink}>
                            <CheckIcon className="checkIcon" />
                          </When>
                          <Otherwise>
                            <Copy />
                          </Otherwise>
                        </Choose>
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
                <If condition={isDirectLinkVisible}>
                  <div className="legacy">
                    <div className="directLink">{localization.DETAILS.textDirectLink}</div>
                    <UpgradePlan
                      text={localization.DETAILS.legacy}
                      isUpgradePlan={false}
                      tooltip={localization.DETAILS.legacyTooltip}
                    />
                  </div>
                  <div className={cn('fieldCopyTo', { isFocused })}>
                    <div className="fieldCopyToHolder">
                      <Input
                        value={thumbnailLink}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        className="fieldCopyToUrl"
                      />
                      <Tooltip content={copied.directLink ? 'Copied!' : 'Copy link'} placement="top">
                        <IconButton
                          onClick={copyDirectLink}
                          size="lg"
                          className="fieldCopyToSettings"
                          componentProps={{
                            'data-value': thumbnailLink,
                            'data-testid': 'iconCopyDirectURL',
                          }}
                        >
                          <Choose>
                            <When condition={copied.directLink}>
                              <CheckIcon className="checkIcon" />
                            </When>
                            <Otherwise>
                              <Copy />
                            </Otherwise>
                          </Choose>
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                </If>
              </If>

            </Otherwise>
          </Choose>
        </>
      </CSSTransition>
    </div>
  );
};

Share.propTypes = {
  selectedAssetsIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  assetsData: PropTypes.shape({
    sharedAsSasCount: PropTypes.number,
    isRestricted: PropTypes.bool,
  }).isRequired,
  isVisible: PropTypes.bool.isRequired,
  inProgress: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  assetSharingAllowed: PropTypes.bool.isRequired,
  asset: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    fileExtension: PropTypes.string,
    singleSharingSettings: PropTypes.shape({
      isShared: PropTypes.bool,
      alias: PropTypes.string,
    }),
    restrictSettings: PropTypes.shape({
      isRestricted: PropTypes.bool,
    }),
  }),
  toggleVisibility: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  isPreview: PropTypes.bool,
};

export default memo(Share);
